import React, { useEffect } from 'react';
import { ReactComponentLike } from 'prop-types';
import { useSendCustomEvent } from '../useSendCustomEvent';
import { useStateContext } from '../../../modules/stateContext';
import {
    useTriggerEventContext,
    useTriggerEventDispatchContext,
} from './triggerEventContext';

type UseSendCustomEventReturnType = ReturnType<typeof useSendCustomEvent>;

type SendCustomEventFunction = UseSendCustomEventReturnType['sendCustomEvent'];

type SendCustomEventFunctionOptions = Parameters<SendCustomEventFunction>[0];

type Event = Pick<SendCustomEventFunctionOptions, 'data' | 'name'>;

type TriggerEventHookFunction = (
    options: SendCustomEventFunctionOptions
) => Promise<void> | void;

type TriggerEventProps<
    ComponentProps extends object = Record<string, never>
> = ComponentProps & {
    component: Exclude<ReactComponentLike, string>;
    event: Event;
    onAfterSendCustomEvent?: TriggerEventHookFunction;
    onBeforeSendCustomEvent?: TriggerEventHookFunction;
    unique?: boolean;
};

export function TriggerEvent<
    ComponentProps extends object = Record<string, never>
>({
    component: Component,
    event,
    onAfterSendCustomEvent,
    onBeforeSendCustomEvent,
    unique,
    ...props
}: TriggerEventProps<ComponentProps>) {
    const { data, name } = event;

    const {
        clientId,
        isFederated,
        ptrt: { value: ptrt },
        realm,
        reverbDestination,
        sequenceId,
        userOrigin,
    } = useStateContext();

    const { events } = useTriggerEventContext();

    const dispatchTriggerEventContext = useTriggerEventDispatchContext();

    const { sendCustomEvent } = useSendCustomEvent();

    async function handleSendCustomEvent() {
        const options: SendCustomEventFunctionOptions = {
            clientId,
            data,
            isFederated,
            name,
            ptrt,
            realm,
            reverbDestination,
            sequenceId,
            userOrigin,
        };

        const eventTriggered = events.some(
            ({ name: eventName, sequenceId: eventSequenceId }) =>
                eventName === name && eventSequenceId === sequenceId
        );

        if (!unique || !eventTriggered) {
            try {
                await onBeforeSendCustomEvent?.(options);

                sendCustomEvent(options);
                dispatchTriggerEventContext({
                    event: {
                        name,
                        sequenceId,
                    },
                    type: 'eventTriggered',
                });

                await onAfterSendCustomEvent?.(options);
            } catch (error) {
                if (process.env.NODE_ENV !== 'production') {
                    // eslint-disable-next-line no-console
                    console.error(
                        `Unable to send custom event: ${
                            typeof error === 'object' &&
                            error &&
                            'message' in error
                                ? error.message
                                : String(error)
                        }`
                    );
                }
            }
        }
    }

    useEffect(() => {
        handleSendCustomEvent();
    }, []);

    return <Component {...props} />;
}
